import React, { createContext, useContext, useState, ReactNode, useCallback } from 'react';

import { ToastProps } from '../components/ui/Toast';
import ToastContainer from '../components/ui/ToastContainer';

interface ToastContextType {
  showToast: (options: Omit<ToastProps, 'id' | 'onClose'>) => string;
  showSuccess: (message: string, title?: string, options?: Partial<ToastOptions>) => string;
  showError: (message: string, title?: string, options?: Partial<ToastOptions>) => string;
  showWarning: (message: string, title?: string, options?: Partial<ToastOptions>) => string;
  showInfo: (message: string, title?: string, options?: Partial<ToastOptions>) => string;
  removeToast: (id: string) => void;
  clearAllToasts: () => void;
}

interface ToastOptions {
  duration: number;
  action?: {
    label: string;
    onClick: () => void;
  };
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

interface ToastProviderProps {
  children: ReactNode;
}

export const ToastProvider: React.FC<ToastProviderProps> = ({ children }) => {
  const [toasts, setToasts] = useState<ToastProps[]>([]);

  const generateId = useCallback(() => {
    return `toast-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }, []);

  const removeToast = useCallback((id: string) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  }, []);

  const showToast = useCallback(
    (options: Omit<ToastProps, 'id' | 'onClose'>) => {
      const id = generateId();
      const toast: ToastProps = {
        ...options,
        id,
        onClose: removeToast,
      };

      setToasts(prev => [...prev, toast]);
      return id;
    },
    [generateId, removeToast]
  );

  const showSuccess = useCallback(
    (message: string, title?: string, options?: Partial<ToastOptions>) => {
      return showToast({
        type: 'success',
        title,
        message,
        duration: options?.duration || 4000,
        action: options?.action,
      });
    },
    [showToast]
  );

  const showError = useCallback(
    (message: string, title?: string, options?: Partial<ToastOptions>) => {
      return showToast({
        type: 'error',
        title,
        message,
        duration: options?.duration || 6000,
        action: options?.action,
      });
    },
    [showToast]
  );

  const showWarning = useCallback(
    (message: string, title?: string, options?: Partial<ToastOptions>) => {
      return showToast({
        type: 'warning',
        title,
        message,
        duration: options?.duration || 5000,
        action: options?.action,
      });
    },
    [showToast]
  );

  const showInfo = useCallback(
    (message: string, title?: string, options?: Partial<ToastOptions>) => {
      return showToast({
        type: 'info',
        title,
        message,
        duration: options?.duration || 4000,
        action: options?.action,
      });
    },
    [showToast]
  );

  const clearAllToasts = useCallback(() => {
    setToasts([]);
  }, []);

  const value: ToastContextType = {
    showToast,
    showSuccess,
    showError,
    showWarning,
    showInfo,
    removeToast,
    clearAllToasts,
  };

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastContainer toasts={toasts} onRemoveToast={removeToast} />
    </ToastContext.Provider>
  );
};

export const useToast = (): ToastContextType => {
  const context = useContext(ToastContext);
  if (context === undefined) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

export default ToastContext;
